const API_BASE_URL = 'http://localhost:8000/api/v1';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'user' | 'superadmin';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ApiKey {
  id: string;
  name: string;
  description?: string;
  keyPreview: string; // e.g. "sk-abc...xyz"
  plainKey?: string; // Only returned once on creation/regeneration
  scopes: string[];
  isActive: boolean;
  neverExpires: boolean;
  expiresAt?: string;
  lastUsedAt?: string;
  usageCount: number;
  createdAt: string;
}

export interface CreateUserDto {
  email: string;
  firstName: string;
  lastName: string;
  password?: string;
  role?: 'admin' | 'user';
}

export interface CreateApiKeyDto {
  name: string;
  description?: string;
  scopes?: string[];
  appScopes?: string[]; // Apps this key can access: ['admin', 'training', 'demo']
  neverExpires?: boolean;
  expiresAt?: string;
}

// Stats types
export interface UserStats {
  total: number;
  active: number;
  admins: number;
  createdLastWeek: number;
}

export interface ApiKeyStats {
  total: number;
  active: number;
  expired: number;
  neverExpires: number;
}

// Helpers
async function fetchWithAuth(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `API Error: ${response.statusText}`);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return null;
  }

  return response.json();
}

// --- Users API ---

export async function fetchUsers(params?: { search?: string }): Promise<{ users: User[]; total: number }> {
  const query = new URLSearchParams();
  if (params?.search) query.append('search', params.search);
  return fetchWithAuth(`/users?${query.toString()}`);
}

export async function fetchUserStats(): Promise<UserStats> {
  return fetchWithAuth('/users/stats');
}

export async function createUser(data: CreateUserDto): Promise<User> {
  return fetchWithAuth('/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateUser(id: string, data: Partial<CreateUserDto>): Promise<User> {
  return fetchWithAuth(`/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function toggleUserStatus(id: string): Promise<User> {
  return fetchWithAuth(`/users/${id}/toggle-status`, {
    method: 'POST',
  });
}

export async function deleteUser(id: string): Promise<void> {
  return fetchWithAuth(`/users/${id}`, {
    method: 'DELETE',
  });
}

export async function resetUserPassword(id: string, password: string): Promise<void> {
  return fetchWithAuth(`/users/${id}/reset-password`, {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

// --- API Keys API ---

export async function fetchApiKeys(): Promise<ApiKey[]> {
  const response = await fetchWithAuth('/api-keys');
  // Backend returns { success: true, data: [...] }
  return response.data || response;
}

export async function fetchApiKeyStats(): Promise<ApiKeyStats> {
  const response = await fetchWithAuth('/api-keys/stats');
  // Backend returns { success: true, data: {...} }
  return response.data || response;
}

export async function createApiKey(data: CreateApiKeyDto): Promise<ApiKey & { plainKey: string }> {
  const response = await fetchWithAuth('/api-keys', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  // Response has nested data: { success, message, data: { apiKey, plainKey } }
  return response.data || response;
}

export async function regeneratApiKey(id: string): Promise<ApiKey & { plainKey: string }> {
  const response = await fetchWithAuth(`/api-keys/${id}/regenerate`, {
    method: 'POST',
  });
  // Response has nested data: { success, message, data: { apiKey, plainKey } }
  return response.data || response;
}

// Alias for compatibility if needed (typo fix)
export const regenerateApiKey = regeneratApiKey;

export async function deleteApiKey(id: string): Promise<void> {
  return fetchWithAuth(`/api-keys/${id}`, {
    method: 'DELETE',
  });
}

export async function markApiKeyCopied(_id: string): Promise<void> {
  // Optional: Track copy events for analytics
  // return fetchWithAuth(`/api-keys/${id}/track-copy`, { method: 'POST' });
  return Promise.resolve();
}
